import CloseIcon from '@ordr-as/icons/CloseIcon';
import { formatClock } from '../utils';
import type { ItemTotal } from '../types';

export type RecentRow = {
  id: string;
  orderNo: number | undefined;
  text: string;
  at: string | undefined;
  groupId: string | undefined;
  /** Set for station rows — enables Undo via recall. */
  undoTaskId?: number;
};

type Props = {
  isExpo: boolean;
  itemTotals: ItemTotal[];
  rows: RecentRow[];
  showClose: boolean;
  onClose: () => void;
  onUndo: (taskId: number) => void;
  onOpenRow: (groupId: string) => void;
};

const CloseButton = ({ onClose }: { onClose: () => void }) => (
  <button
    className="flex-none w-6 h-6 rounded-full flex items-center justify-center text-brand-secondary hover:bg-grey-lightest"
    onClick={onClose}
    title="Hide panel"
  >
    <CloseIcon width={18} height={18} />
  </button>
);

const RecentPanel = ({ isExpo, itemTotals, rows, showClose, onClose, onUndo, onOpenRow }: Props) => (
  <>
    {itemTotals.length > 0 && (
      <div className="flex-none flex flex-col min-h-0 max-h-[45%] border-b border-grey-medium">
        <div className="flex items-center justify-between gap-2 pl-4 pr-2 pt-3 pb-2">
          <h3 className="m-0 text-standard font-bold">Item totals</h3>
          {showClose && <CloseButton onClose={onClose} />}
        </div>
        <div className="overflow-y-auto px-4 pb-3 flex flex-col gap-1">
          {itemTotals.map((total) => (
            <div key={total.name} className="flex gap-2 items-baseline text-small">
              <span className="font-bold text-brand-secondary flex-none min-w-5">{total.quantity}×</span>
              <span className="text-brand-black truncate">{total.name}</span>
            </div>
          ))}
        </div>
      </div>
    )}

    <div className="flex items-center justify-between gap-2 pl-4 pr-2 py-3 flex-none">
      <h3 className="m-0 text-standard font-bold">{isExpo ? 'Recently served' : 'Recently done'}</h3>
      {showClose && itemTotals.length === 0 && <CloseButton onClose={onClose} />}
    </div>
    <div className="flex-1 overflow-y-auto px-3 pb-4 flex flex-col gap-2">
      {rows.length === 0 && (
        <div className="text-grey-dark text-small p-3 text-center">
          Nothing yet — completed items will show up here.
        </div>
      )}
      {rows.map((row) => (
        <div
          key={row.id}
          className={`bg-brand-white border border-grey-lightest rounded p-3 flex items-center gap-2 text-small ${
            row.groupId ? 'cursor-pointer' : ''
          }`}
          onClick={row.groupId ? () => onOpenRow(row.groupId as string) : undefined}
        >
          <span className="font-bold text-brand-secondary flex-none">#{row.orderNo}</span>
          <span className="text-brand-black flex-1 truncate">{row.text}</span>
          {row.undoTaskId !== undefined ? (
            <button
              className="flex-none text-extra-small font-semibold text-brand-primary px-1 py-0.5 rounded hover:bg-purple-lightest"
              onClick={(e) => {
                e.stopPropagation();
                onUndo(row.undoTaskId as number);
              }}
            >
              Undo
            </button>
          ) : (
            <span className="flex-none text-extra-small text-grey-dark">{formatClock(row.at)}</span>
          )}
        </div>
      ))}
    </div>
  </>
);

export default RecentPanel;
